<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useApiTabStore } from '@/stores/apiTab.store'
import BaseInput from '@/components/forms/BaseInput.vue'
import { AUTHORIZATION_AUTH_TYPES } from '@/utils/apiTab'
import { RequestSection } from '@/utils/apiTab'

const props = defineProps({
  activeRequestIndex: {
    type: Number,
    required: true
  }
})

const apiTabStore = useApiTabStore()
const bearerToken = ref<string>('')
/**
 * Retrieves a bearer token from the store based on the active request index.
 * If a valid bearer token is found, it updates the `bearerToken` object with
 * the retrieved token. If no valid token is found, it resets the bearer token
 * to an empty string.
 */
const retrieveValuesFromStore = () => {
  const bearerTokenFromStore = apiTabStore.getRequestFieldData(
    props.activeRequestIndex,
    RequestSection.AUTHORIZATION,
    AUTHORIZATION_AUTH_TYPES.BEARER_TOKEN
  )
  if (bearerTokenFromStore && typeof bearerTokenFromStore === 'string') {
    bearerToken.value = (bearerTokenFromStore as string) || ''
  } else {
    bearerToken.value = ''
  }
}

onMounted(() => {
  retrieveValuesFromStore()
})

watch(
  () => bearerToken.value,
  () => {
    apiTabStore.updateRequestField(
      props.activeRequestIndex,
      RequestSection.AUTHORIZATION,
      bearerToken.value,
      AUTHORIZATION_AUTH_TYPES.BEARER_TOKEN
    )
  }
)

watch(
  () => props.activeRequestIndex,
  () => {
    retrieveValuesFromStore()
  }
)
</script>

<template>
  <span class="text-mid text-xs font-extralight tracking-tight">
    The authorization header will be automatically generated when you send the request.
    <a href="https://www.jdoodle.com/docs/ide/api-testing" target="_blank" class="underline"
      >Learn more about authorization.</a
    >
  </span>
  <div class="flex w-full flex-col">
    <BaseInput
      id="bearer-token"
      label="Token"
      :inputType="'text'"
      placeholder="token"
      v-model="bearerToken"
      :isBoldLabel="true"
      :isLightGray="false"
      labelClass="text-xs font-medium text-high"
      isCustomClass="border !border-border-primary outline-none background-primary"
    />
  </div>
</template>
